import CloseIcon from "@mui/icons-material/Close";

import { Box, IconButton, useTheme } from "@mui/material";
import WarningIcon from "apps/web/src/components/universal/WarningIcon";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { roundMacros } from "@notemeal/shared-utils-macro-protocol";
import { useMemo } from "react";
import { getServingAmountsMacros } from "shared-ui/ServingAmount/utils";
import { useMenuBuilderContext } from "../MenuBuilderProvider";
import { MenuBuilderMealRowItemIndexedType } from "./MenuBuilderMealSchema";
import { MenuBuilderMenuItemMacro } from "./MenuBuilderMenuItemMacro";
import { MenuItemMacrosBreakdownTooltip } from "./MenuItemMacrosBreakdownTooltip";
interface Props {
  rowItem: MenuBuilderMealRowItemIndexedType;
  removeRowItem: (index: number) => void;
  setEditMenuItemId: (id: string) => void;
}

export const MenuBuilderRowItem = ({ rowItem, removeRowItem, setEditMenuItemId }: Props) => {
  const {
    palette: { background, greyscale },
  } = useTheme();
  const { isEditable, displayMacros, menuItemDetailsById } = useMenuBuilderContext();

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: rowItem.menuItem.id });

  const fullMenuItem = menuItemDetailsById[rowItem.menuItem.id];
  const macros = useMemo(() =>
    fullMenuItem?.servingAmounts ? roundMacros(getServingAmountsMacros(fullMenuItem.servingAmounts)) : null,
    [fullMenuItem?.servingAmounts]
  );

  const macrosSection = useMemo(() => {
    if (!displayMacros) return null;
    return (
      <>
        {macros && <MenuBuilderMenuItemMacro macros={macros} boxStyle={{ my: 1 }} />}
        {fullMenuItem?.choices && fullMenuItem.choices?.length > 0 && <MenuItemMacrosBreakdownTooltip menuItem={fullMenuItem} />}
      </>
    );
  }, [displayMacros, macros, fullMenuItem]);

  return (
    <Box
      key={rowItem.position}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        flexDirection: "column",
        border: "1px solid #D2D9DA",
        borderRadius: "6px",
        p: 1,
        position: "relative",
        width: "200px",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: greyscale[100],
        },
        backgroundColor: background.default,
        // drag and drop
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      onClick={() => setEditMenuItemId(rowItem.menuItem.id)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" }}>
        <Box
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          sx={{
            fontSize: "14px",
            lineHeight: "18px",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            width: "160px",
          }}
        >
          <Box sx={{ display: "flex" }}>
            {rowItem.menuItem.isMissingIngredients && <WarningIcon sx={{ fontSize: "16px", mr: 0.5 }} tip="Missing Ingredients" />}
            <Box sx={{ userSelect: "none" }}>{rowItem.menuItem.name}</Box>
          </Box>
        </Box>
        {isEditable && (
          <IconButton
            sx={{
              position: "absolute",
              top: 1,
              right: 1,
            }}
            onClick={e => {
              e.stopPropagation();
              removeRowItem(rowItem.index);
            }}
            aria-label="Remove"
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        )}
      </Box>
      {macrosSection}
    </Box>
  );
};
